import { api } from './api-client';

/**
 * The marketing site's waitlist, as `/v1/admin/waitlist` serves it.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/waitlist/`.
 *
 * **There is no `unsubscribeToken` in these types, and there must never be one.**
 * That token is the only handle a person on this list has over their own row —
 * they have no account and no session — and the API deliberately never reads it
 * out of Postgres for this surface. If one ever appears in a response, the bug
 * is on the API side and this file is not the place to paper over it.
 */
export interface WaitlistSignup {
  id: string;
  email: string;
  /** Which surface they came from. One value today. */
  source: string;
  /** When they asked to be left out of the launch email, or null. */
  unsubscribedAt: string | null;
  /** When the launch email was sent to them, or null. */
  notifiedAt: string | null;
  createdAt: string;
}

/**
 * Which slice to list.
 *
 * `notified` is **not** part of the consent partition — a notified row can be
 * either subscribed or unsubscribed. It is a filter, and the screen says so
 * rather than letting the control imply four mutually exclusive states.
 */
export type WaitlistFilter =
  | 'subscribed'
  | 'unsubscribed'
  | 'notified'
  | 'all';

/**
 * Named for the rows rather than for the screen: the route component is
 * `WaitlistPage`, and two `WaitlistPage`s in one import would collide.
 */
export interface SignupPage {
  signups: WaitlistSignup[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WaitlistSummary {
  total: number;
  last7Days: number;
}

export interface ListWaitlistQuery {
  status?: WaitlistFilter;
  q?: string;
  page?: number;
  pageSize?: number;
}

/**
 * The query string is built here rather than in `api-client.ts`, the same way
 * `users.ts` does it — and only keys with a value are sent, because
 * `forbidNonWhitelisted` on the API makes a stray parameter a 400 rather than
 * something quietly ignored.
 */
export async function listWaitlist(
  query: ListWaitlistQuery = {},
): Promise<SignupPage> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return api.get<SignupPage>(`/admin/waitlist${suffix}`);
}

export function getWaitlistSummary(): Promise<WaitlistSummary> {
  return api.get<WaitlistSummary>('/admin/waitlist/summary');
}

/**
 * Sets or clears the unsubscribe flag. Recorded in the admin audit log against
 * the operator who called it.
 */
export async function setWaitlistUnsubscribed(
  id: string,
  unsubscribed: boolean,
): Promise<WaitlistSignup> {
  const { signup } = await api.patch<{ signup: WaitlistSignup }>(
    `/admin/waitlist/${id}`,
    { unsubscribed },
  );
  return signup;
}

/**
 * Deletes a signup. Recorded in the admin audit log — **including the address**,
 * which is why this removes somebody from the mailing list rather than erasing
 * them from the database. The confirmation copy says so.
 */
export function deleteWaitlistSignup(id: string): Promise<void> {
  return api.del(`/admin/waitlist/${id}`);
}
